import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { fetchPrototype, parsePrototypeFilename } from '../lib/prototypeFetch';
import LegalLinks from '../components/LegalLinks';
import { isWaitingOnBusinessDecision, WAITING_ON_BUSINESS_DECISION_LABEL } from '../lib/nfrStatus';

// #2331 — the project_members-only counterpart to ClientDashboard.tsx.
// A caller invited via the project-team `projectInvitations` flow (no
// `tenant_members` row, no `project_stakeholders` row) already has SERVER
// access to these projects' open-questions and prototype-view routes via
// assertArtefactProjectOrTenantAccess's project_members branch — this page
// is the missing front door for that existing access, not a new grant.
//
// Deliberately NOT a mirror of ClientDashboard's approval/sign-off/CO
// sections: those all read through assertArtefactTenantWriteAccess- or
// requireTenantMember-backed routes this caller's shape 403s on (see
// RefinementSuggestionsPage.tsx's own docstring — its GET/PATCH routes gate
// on assertArtefactTenantWriteAccess, which 403s a project_members-only
// caller regardless of what this page links to). Links ONLY into routes
// this caller's shape is actually admitted to: Open Questions and the
// prototype view/download route. No refinement-suggestions link — the only
// project_members-accessible route in that family is a POST capture
// trigger with no dedicated page; the page a user would expect ("review
// suggestions") is founder-only by design.
//
// Deliberately not wrapped in ProjectLayout/Sidebar (contrast the founder-
// side /projects/:projectId/* tree): this page fetches only its own data
// and needs no shared chrome. This does NOT avoid ProjectPhaseStepper
// entirely, though — R1 review (#2331) caught that the "Open Questions"
// button below navigates INTO /projects/:projectId/open-questions, which
// App.tsx nests under ProjectLayout regardless of where the click
// originated, so the stepper mounts anyway on that destination page.
// ProjectPhaseStepper reads GET /projects/:projectId/phase-states
// (requireTenantMember-gated, phase-transitions.ts), which 403s this
// caller shape (no tenant_members row) — fixed at the source instead
// (ProjectPhaseStepper.tsx now renders nothing on a fetch error rather
// than an indefinite loading skeleton), so this is fine as-is.
//
// VIEWING MECHANISM mirrors PrototypeViewer.tsx exactly (shared fetch via
// lib/prototypeFetch.ts): a sandboxed (`sandbox=""`, no allow-scripts, no
// allow-same-origin) iframe loaded from a same-origin Blob URL, never a
// plain new-tab navigation (this app has no auth cookie to carry) and never
// `window.open`ed directly (a blob: URL opened that way inherits this
// page's own origin for scripting). See PrototypeViewer.tsx's top-of-file
// comment for the full reasoning this reuses.

interface PrototypeRef {
  artefactId: string;
  versionId: string;
}

interface MemberProject {
  projectId: string;
  projectName: string;
  domain: string | null;
  projectMemberId: string;
  prototype: PrototypeRef | null;
}

// #2741 — PM-owns-and-delegates ownership model. The one genuinely new
// client-portal surface the story's recon flagged: before the access-gate
// migration (client.routes.ts's own #2741 comment), a project_members-only
// caller couldn't even reach /projects/:projectId/risks or /nfrs at all, so
// there was nowhere to show this regardless of whether assignment existed.
//
// #2761 — the endpoint now returns one `items` array with a sourceType
// discriminator and a normalized `title` (type-specific fields preserved
// underneath). Rendered in the order the API returns: OQ, Risk, NFR.
type AssignedSourceType = 'OPEN_QUESTION' | 'RISK' | 'NFR';
interface AssignedItem { id: string; projectId: string; projectName: string; handle: string | null; status: string; title: string; sourceType: AssignedSourceType; }
interface AssignedItems { items: AssignedItem[]; }

// Per-sourceType testid + the project sub-page "act on this" links out to.
const ASSIGNED_ROUTE: Record<AssignedSourceType, { testId: string; path: string }> = {
  OPEN_QUESTION: { testId: 'assigned-open-question', path: 'open-questions' },
  RISK:          { testId: 'assigned-risk',          path: 'risks' },
  NFR:           { testId: 'assigned-nfr',           path: 'nfrs' },
};

// #2761 / #2835 / #2834 — the waiting-label rule and its wording live in
// lib/nfrStatus.ts (isWaitingOnBusinessDecision, WAITING_ON_BUSINESS_DECISION_LABEL),
// shared with the Workbasket so the two lists can't drift apart.

export default function ProjectMemberDashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<MemberProject[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [assigned, setAssigned] = useState<AssignedItems | null>(null);

  const [openProjectId, setOpenProjectId] = useState<string | null>(null);
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);
  const [prototypeBusy, setPrototypeBusy] = useState<string | null>(null);
  const [prototypeError, setPrototypeError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ projects: MemberProject[] }>('/client/my-member-projects')
      .then(({ projects: rows }) => { if (!cancelled) setProjects(rows); })
      .catch((err: Error) => { if (!cancelled) setLoadError(err.message); });
    return () => { cancelled = true; };
  }, []);

  // #2741 — a separate, independently-failing fetch: this section is
  // additive to the page's existing content, so an /my-assigned-items
  // error must not block the projects list above from rendering.
  useEffect(() => {
    let cancelled = false;
    apiFetch<AssignedItems>('/client/my-assigned-items')
      // #2761 — shape-checked so an unexpected body (e.g. the pre-#2761
      // three-array shape from an API mid-deploy) omits the section rather
      // than crashing the render on `items.length`.
      .then((data) => { if (!cancelled && Array.isArray(data?.items)) setAssigned(data); })
      .catch(() => { /* silently omit the section on failure — non-critical */ });
    return () => { cancelled = true; };
  }, []);

  // Revoke the previous Blob URL whenever it's replaced or on unmount —
  // mirrors PrototypeViewer.tsx's own cleanup effect exactly.
  useEffect(() => {
    return () => { if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl); };
  }, [previewObjectUrl]);

  function closePreview() {
    setOpenProjectId(null);
    if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl);
    setPreviewObjectUrl(null);
  }

  async function handleViewPrototype(project: MemberProject) {
    if (!project.prototype) return;
    if (openProjectId === project.projectId) { closePreview(); return; }

    setPrototypeBusy(project.projectId);
    setPrototypeError(null);
    try {
      const res = await fetchPrototype(project.prototype.artefactId, project.prototype.versionId);
      const html = await res.text();
      if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl);
      setPreviewObjectUrl(URL.createObjectURL(new Blob([html], { type: 'text/html' })));
      setOpenProjectId(project.projectId);
    } catch (err) {
      setPrototypeError(err instanceof Error ? err.message : 'Failed to open prototype');
    } finally {
      setPrototypeBusy(null);
    }
  }

  async function handleDownloadPrototype(project: MemberProject) {
    if (!project.prototype) return;
    setPrototypeBusy(`download-${project.projectId}`);
    setPrototypeError(null);
    try {
      const res = await fetchPrototype(project.prototype.artefactId, project.prototype.versionId, { download: true });
      const filename = parsePrototypeFilename(res.headers.get('content-disposition'), 'prototype.html');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setPrototypeError(err instanceof Error ? err.message : 'Failed to download prototype');
    } finally {
      setPrototypeBusy(null);
    }
  }

  const linkStyle: React.CSSProperties = {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    fontSize: 13, color: 'var(--primary)', fontWeight: 500, whiteSpace: 'nowrap',
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: 32 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, fontFamily: 'var(--font-heading)' }}>
        Your Projects
      </h1>
      <p style={{ color: 'var(--text-secondary, #6b7280)', marginBottom: 32, fontSize: 14 }}>
        Open Questions and the latest prototype for each project you&apos;re a member of.
      </p>

      {assigned && assigned.items.length > 0 && (
        <section
          data-testid="assigned-to-you"
          style={{
            padding: 20, borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--surface)', marginBottom: 24,
          }}
        >
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, fontFamily: 'var(--font-heading)' }}>
            Assigned to you
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {assigned.items.map((item) => (
              <button
                key={`${item.sourceType}:${item.id}`}
                type="button"
                data-testid={ASSIGNED_ROUTE[item.sourceType].testId}
                onClick={() => navigate(`/projects/${item.projectId}/${ASSIGNED_ROUTE[item.sourceType].path}`)}
                style={{ ...linkStyle, textAlign: 'left', display: 'block' }}
              >
                {item.handle ? `${item.handle}: ` : ''}{item.title} <span style={{ color: 'var(--text-secondary, #6b7280)' }}>— {item.projectName}</span>
                {/* explicit space: JSX drops the line-break whitespace, which ran
                    "…Project" and the waiting label together in the accessible name */}
                {isWaitingOnBusinessDecision(item) && ' '}
                {isWaitingOnBusinessDecision(item) && (
                  <span
                    data-testid="assigned-waiting-on-business-decision"
                    style={{
                      marginLeft: 8, padding: '1px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                      background: 'rgba(99,102,241,0.12)', color: 'var(--primary)',
                    }}
                  >
                    {WAITING_ON_BUSINESS_DECISION_LABEL}
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      {loadError && (
        <p style={{ color: 'var(--error, #dc2626)', fontSize: 14 }}>{loadError}</p>
      )}

      {!loadError && projects === null && (
        <p style={{ color: 'var(--text-secondary, #6b7280)', fontSize: 14 }}>Loading…</p>
      )}

      {!loadError && projects !== null && projects.length === 0 && (
        <p style={{ color: 'var(--text-secondary, #6b7280)', fontSize: 14 }}>
          No projects found for your account.
        </p>
      )}

      {projects !== null && projects.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {projects.map((project) => (
            <section
              key={project.projectId}
              data-testid="member-project-card"
              style={{
                padding: 20, borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--surface)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <span style={{ fontSize: 18, fontWeight: 700 }}>{project.projectName}</span>
                {project.domain && (
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 9999,
                    backgroundColor: 'rgba(124,58,237,0.1)', color: '#7c3aed', fontWeight: 500,
                  }}>
                    {project.domain}
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <button
                  type="button"
                  data-testid="member-open-questions-link"
                  onClick={() => navigate(`/projects/${project.projectId}/open-questions`)}
                  style={linkStyle}
                >
                  Open Questions
                </button>

                {project.prototype && (
                  <>
                    <button
                      type="button"
                      data-testid="member-view-prototype"
                      onClick={() => void handleViewPrototype(project)}
                      disabled={prototypeBusy === project.projectId}
                      style={linkStyle}
                    >
                      {prototypeBusy === project.projectId
                        ? 'Opening…'
                        : openProjectId === project.projectId ? 'Close preview' : 'View Prototype'}
                    </button>
                    <button
                      type="button"
                      data-testid="member-download-prototype"
                      onClick={() => void handleDownloadPrototype(project)}
                      disabled={prototypeBusy === `download-${project.projectId}`}
                      style={linkStyle}
                    >
                      {prototypeBusy === `download-${project.projectId}` ? 'Downloading…' : 'Download'}
                    </button>
                  </>
                )}
              </div>

              {openProjectId === project.projectId && (
                <div style={{ marginTop: 16 }}>
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                    Sandboxed preview — scripted interactivity is disabled here for safety. Download the file to view it with full interactivity in your own browser.
                  </p>
                  <iframe
                    title={`${project.projectName} prototype preview`}
                    data-testid="member-prototype-preview-frame"
                    sandbox=""
                    referrerPolicy="no-referrer"
                    src={previewObjectUrl ?? undefined}
                    style={{ width: '100%', height: 600, border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }}
                  />
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {prototypeError && (
        <p role="alert" style={{ color: 'var(--error, #dc2626)', fontSize: 13, marginTop: 16 }}>{prototypeError}</p>
      )}

      {/* #2504 (RZP.C1) — Terms/Privacy. As above: a project_members-only
          account has no tenantId claim and is redirected away from /settings,
          so this is their only reachable surface. LegalLinks carries its own
          styling because this page is deliberately not wrapped in
          ProjectLayout/Sidebar (see the header note). */}
      <LegalLinks style={{ marginTop: 32 }} />
    </div>
  );
}
